import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Grid,
  Statistic,
  Table,
  Button,
  Icon,
  Input,
  Dropdown,
  Form,
  Progress,
  Label,
  Segment,
  Header,
  Message,
} from "semantic-ui-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { ObjectiveMatrix, StrategicAxis } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { toast } from "react-toastify";
import "./StrategyObjectivesMatrix.scss";

const objectiveMatrixController = new ObjectiveMatrix();
const strategicAxisController = new StrategicAxis();

const ESTADOS = ["Planned", "InProgress", "Delayed", "Completed"];
const ETAPAS_PDCA = ["Plan", "Do", "Check", "Act"];

const DEFAULT_OBJ = {
  axis: "",
  objective: "",
  kpi: "",
  unit: "",
  baseline: "",
  target: "",
  deadline: "",
  responsible: "",
  relevance: 3,
  feasibility: 3,
  actionPlan: "",
  budget: "",
  progress: 0,
  status: "Planned",
  evidence: "",
  stage: "Plan",
};

function calculateSMART(obj) {
  const S = Boolean((obj.objective || "").trim().length > 0);
  const M = Boolean(
    (obj.kpi || "").trim() && (obj.unit || "").trim() && (obj.target || "").match(/[0-9]/)
  );
  const A = Number(obj.feasibility || 0) >= 3;
  const R = Number(obj.relevance || 0) >= 3;
  const T = Boolean(obj.deadline);
  const score = [S, M, A, R, T].filter(Boolean).length;
  return { S, M, A, R, T, score };
}

export default function StrategyObjectivesMatrix({ site }) {
  const { accessToken } = useAuth();
  const [matrix, setMatrix] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [axes, setAxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_OBJ);
  const [filterEje, setFilterEje] = useState("All");
  const [filterEstado, setFilterEstado] = useState("All");
  const [searchText, setSearchText] = useState("");

  const currentYear = new Date().getFullYear().toString();

  // Cargar ejes estratégicos
  useEffect(() => {
    if (!site || !accessToken) return;
    (async () => {
      try {
        const response = await strategicAxisController.getAxesBySite(accessToken, site);
        if (response.code === 200 && response.axes) {
          setAxes(response.axes.map((a) => a.name));
        }
      } catch (error) {
        console.error("Error al cargar ejes:", error);
        setAxes([
          "Agua",
          "Energía/GEI",
          "Residuos",
          "Químicos/ZDHC",
          "OHS/Seguridad",
          "Cumplimiento/Legal",
          "Comunidad/Biodiversidad",
        ]);
      }
    })();
  }, [site, accessToken]);

  // Cargar matriz
  useEffect(() => {
    if (!site || !accessToken) return;
    loadMatrix();
  }, [site, accessToken]);

  const loadMatrix = async () => {
    setLoading(true);
    try {
      const response = await objectiveMatrixController.getMatrixBySiteAndYear(
        accessToken,
        site,
        currentYear
      );
      if (response.code === 200 && response.matrix) {
        setMatrix(response.matrix);
        setObjectives(response.matrix.objectives || []);
      }
    } catch (error) {
      if (error.code === 404) {
        console.log("No existe matriz para este año");
      } else {
        console.error("Error al cargar matriz:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Estadísticas calculadas
  const stats = useMemo(() => {
    const total = objectives.length;
    const cumplidos = objectives.filter((o) => o.estado === "Cumplido").length;
    const enCurso = objectives.filter((o) => o.estado === "En curso").length;
    const retrasados = objectives.filter((o) => o.estado === "Retrasado").length;
    const avgSMART =
      total > 0
        ? objectives.reduce((sum, o) => sum + calculateSMART(o).score, 0) / total
        : 0;
    const avgAvance = total > 0 ? objectives.reduce((sum, o) => sum + o.progress, 0) / total : 0;

    return { total, cumplidos, enCurso, retrasados, avgSMART, avgAvance };
  }, [objectives]);

  // Datos para gráfico de barras (agrupado por eje)
  const chartData = useMemo(() => {
    const grouped = {};
    axes.forEach((axis) => {
      grouped[axis] = objectives.filter((o) => o.axis === axis);
    });

    return axes.map((axis) => {
      const objs = grouped[axis] || [];
      const avgAvance = objs.length > 0 ? objs.reduce((s, o) => s + o.progress, 0) / objs.length : 0;
      return {
        axis: axis.length > 15 ? axis.substring(0, 12) + "..." : axis,
        "Avance %": Math.round(avgAvance),
      };
    });
  }, [objectives, axes]);

  // Objetivos filtrados
  const filteredObjectives = useMemo(() => {
    return objectives.filter((obj) => {
      if (filterEje !== "All" && obj.axis !== filterEje) return false;
      if (filterEstado !== "All" && obj.status !== filterEstado) return false;
      if (searchText) {
        const txt = searchText.toLowerCase();
        return (
          (obj.objective || "").toLowerCase().includes(txt) ||
          (obj.kpi || "").toLowerCase().includes(txt) ||
          (obj.responsible || "").toLowerCase().includes(txt)
        );
      }
      return true;
    });
  }, [objectives, filterEje, filterEstado, searchText]);

  // Guardar objetivo (solo crear - editar se hace en modal)
  const handleSaveObjective = async () => {
    if (!formData.axis || !formData.objective) {
      toast.error("Debe completar al menos el Eje y el Objetivo");
      return;
    }

    setLoading(true);
    try {
      // Crear nuevo objetivo
      // Primero verificar si existe matriz para este sitio/año
      let matrixId = matrix?._id;

      if (!matrixId) {
        // Intentar obtener matriz existente
        try {
          const checkResponse = await objectiveMatrixController.getMatrixBySiteAndYear(
            accessToken,
            site,
            currentYear
          );
          if (checkResponse.code === 200 && checkResponse.matrix) {
            matrixId = checkResponse.matrix._id;
            setMatrix(checkResponse.matrix);
          }
        } catch (checkError) {
          // No existe matriz, se creará después
        }
      }

      if (matrixId) {
        // Existe matriz, agregar objetivo
        const response = await objectiveMatrixController.addObjective(
          accessToken,
          matrixId,
          formData
        );
        if (response.code === 201 || response.code === 200) {
          toast.success("Objetivo agregado");
          await loadMatrix();
          setFormData(DEFAULT_OBJ); // Limpiar formulario
        }
      } else {
        // No existe matriz, crear nueva con el primer objetivo
        const createRes = await objectiveMatrixController.createMatrix(accessToken, {
          site,
          year: currentYear,
          objectives: [formData],
        });
        if (createRes.code === 201) {
          toast.success("Matriz y objetivo creados");
          await loadMatrix();
          setFormData(DEFAULT_OBJ); // Limpiar formulario
        }
      }
    } catch (error) {
      console.error("Error al guardar objetivo:", error);

      // Mostrar mensaje de error específico si es error 409
      if (error.code === 409) {
        toast.error("Ya existe una matriz para este año. Recargando datos...");
        await loadMatrix();
      } else {
        toast.error(error.message || "Error al guardar objetivo");
      }
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteObjective = async (objId) => {
    if (!window.confirm("¿Está seguro de eliminar este objetivo?")) return;
    setLoading(true);
    try {
      const response = await objectiveMatrixController.deleteObjective(
        accessToken,
        matrix._id,
        objId
      );
      if (response.code === 200) {
        toast.success("Objetivo eliminado");
        await loadMatrix();
      }
    } catch (error) {
      console.error("Error al eliminar:", error);
      toast.error("Error al eliminar objetivo");
    } finally {
      setLoading(false);
    }
  };

  // Copiar objetivo existente
  const handleCopyObjective = async (obj) => {
    const copiedData = {
      ...obj,
      objective: `${obj.objective} (copia)`,
      progress: 0,
      status: "Planned",
      stage: "Plan",
    };
    // Eliminar campos que no deben copiarse
    delete copiedData._id;
    delete copiedData.createdAt;
    delete copiedData.updatedAt;

    setLoading(true);
    try {
      let matrixId = matrix?._id;
      if (matrixId) {
        const response = await objectiveMatrixController.addObjective(
          accessToken,
          matrixId,
          copiedData
        );
        if (response.code === 201 || response.code === 200) {
          toast.success("Objetivo copiado");
          await loadMatrix();
        }
      }
    } catch (error) {
      console.error("Error al copiar objetivo:", error);
      toast.error("Error al copiar objetivo");
    } finally {
      setLoading(false);
    }
  };

  // Actualizar campo individual inline
  const handleInlineUpdate = async (objId, field, value) => {
    try {
      const response = await objectiveMatrixController.updateObjective(
        accessToken,
        matrix._id,
        objId,
        { [field]: value }
      );
      if (response.code === 200) {
        // Actualizar localmente sin recargar todo
        setObjectives((prev) =>
          prev.map((o) => (o._id === objId ? { ...o, [field]: value } : o))
        );
        toast.success("Actualizado");
      }
    } catch (error) {
      console.error("Error al actualizar:", error);
      toast.error("Error al actualizar");
    }
  };

  const axisOptions = axes.map((a) => ({ key: a, text: a, value: a }));
  const estadoOptions = ESTADOS.map((e) => ({ key: e, text: e, value: e }));
  const etapaOptions = ETAPAS_PDCA.map((e) => ({ key: e, text: e, value: e }));

  const getEstadoColor = (estado) => {
    switch (estado) {
      case "Completed":
        return "green";
      case "Delayed":
        return "red";
      case "InProgress":
        return "yellow";
      default:
        return "grey";
    }
  };

  const getSMARTColor = (score) => {
    if (score === 5) return "green";
    if (score >= 3) return "yellow";
    return "red";
  };

  return (
    <div className="strategy-objectives-matrix">
      {/* Dashboard de estadísticas */}
      <Grid columns={6} stackable>
        <Grid.Column>
          <Card fluid>
            <Card.Content textAlign="center">
              <Statistic size="small">
                <Statistic.Value>{stats.total}</Statistic.Value>
                <Statistic.Label>Total objetivos</Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
        <Grid.Column>
          <Card fluid>
            <Card.Content textAlign="center">
              <Statistic size="small" color="orange">
                <Statistic.Value>{Math.round(stats.avgAvance)}%</Statistic.Value>
                <Statistic.Label>Avance promedio</Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
        <Grid.Column>
          <Card fluid>
            <Card.Content textAlign="center">
              <Statistic size="small" color="green">
                <Statistic.Value>{stats.cumplidos}</Statistic.Value>
                <Statistic.Label>Cumplidos</Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
        <Grid.Column>
          <Card fluid>
            <Card.Content textAlign="center">
              <Statistic size="small" color="yellow">
                <Statistic.Value>{stats.enCurso}</Statistic.Value>
                <Statistic.Label>En curso</Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
        <Grid.Column>
          <Card fluid>
            <Card.Content textAlign="center">
              <Statistic size="small" color="red">
                <Statistic.Value>{stats.retrasados}</Statistic.Value>
                <Statistic.Label>Retrasados</Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
        <Grid.Column>
          <Card fluid>
            <Card.Content textAlign="center">
              <Statistic size="small" color={getSMARTColor(stats.avgSMART)}>
                <Statistic.Value>{stats.avgSMART.toFixed(1)}</Statistic.Value>
                <Statistic.Label>SMART promedio (0-5)</Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
      </Grid>

      {/* Gráfico de avance por eje */}
      <Segment style={{ marginTop: "20px" }}>
        <Header as="h3">Avance por Eje Estratégico</Header>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="axis" angle={-45} textAnchor="end" height={100} />
            <YAxis domain={[0, 100]} />
            <Tooltip />
            <Legend />
            <Bar dataKey="Avance %" fill="#2185d0" />
          </BarChart>
        </ResponsiveContainer>
      </Segment>

      {/* Segment con Cards de Objetivos */}
      <Segment style={{ marginTop: "20px" }}>
        <Header as="h3">Objetivos Estratégicos</Header>
        {filteredObjectives.length === 0 ? (
          <Message info>
            <Message.Header>No hay objetivos</Message.Header>
            <p>Comience agregando objetivos usando el formulario de abajo.</p>
          </Message>
        ) : (
          <Card.Group itemsPerRow={3} stackable>
            {filteredObjectives.map((obj) => {
              const smart = calculateSMART(obj);
              return (
                <Card key={obj._id}>
                  <Card.Content>
                    <Card.Header>
                      {obj.objective}
                      <Label
                        color={smart.score === 5 ? "green" : smart.score >= 3 ? "yellow" : "red"}
                        size="tiny"
                        style={{ float: "right" }}
                      >
                        {smart.score}/5
                      </Label>
                    </Card.Header>
                    <Card.Meta>
                      <Icon name="tag" /> {obj.axis}
                    </Card.Meta>
                    <Card.Description>
                      <Progress
                        percent={obj.progress || 0}
                        size="small"
                        color={obj.progress >= 100 ? "green" : obj.progress >= 50 ? "blue" : "orange"}
                        progress
                      />
                      <div style={{ marginTop: "10px" }}>
                        <Label size="small" color={getEstadoColor(obj.status)}>
                          {obj.status}
                        </Label>
                      </div>
                    </Card.Description>
                  </Card.Content>
                </Card>
              );
            })}
          </Card.Group>
        )}
      </Segment>

      {/* Sección de búsqueda y filtros */}
      <Segment style={{ marginTop: "20px" }}>
        <Header as="h3">Buscar y Filtrar</Header>
        <Grid columns={3} stackable>
          <Grid.Column>
            <Input
              icon="search"
              placeholder="Buscar objetivo, KPI, plan, responsable..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              fluid
            />
          </Grid.Column>
          <Grid.Column>
            <Dropdown
              placeholder="Todos los ejes"
              selection
              options={[{ key: "All", text: "All", value: "All" }, ...axisOptions]}
              value={filterEje}
              onChange={(e, { value }) => setFilterEje(value)}
              fluid
            />
          </Grid.Column>
          <Grid.Column>
            <Dropdown
              placeholder="Todos los estados"
              selection
              options={[
                { key: "All", text: "All", value: "All" },
                ...estadoOptions,
              ]}
              value={filterEstado}
              onChange={(e, { value }) => setFilterEstado(value)}
              fluid
            />
          </Grid.Column>
        </Grid>
      </Segment>

      {/* Formulario para agregar objetivo */}
      <Segment style={{ marginTop: "20px" }}>
        <Header as="h4">
          <Icon name="plus circle" />
          Agregar objetivo (Asistente SMART + PDCA)
        </Header>
        <Form loading={loading}>
          <Form.Group widths="equal">
            <Form.Field required>
              <label>Eje</label>
              <Dropdown
                placeholder="Seleccione eje..."
                selection
                options={axisOptions}
                value={formData.axis}
                onChange={(e, { value }) => setFormData({ ...formData, axis: value })}
              />
            </Form.Field>
            <Form.Input
              label="Responsable"
              value={formData.responsible}
              onChange={(e) => setFormData({ ...formData, responsible: e.target.value })}
            />
          </Form.Group>

          <Form.TextArea
            label="Objetivo (específico)"
            placeholder="Describa el objetivo de forma clara y específica..."
            value={formData.objective}
            onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
            required
          />

          <Form.Group widths="equal">
            <Form.Input
              label="KPI (medible)"
              placeholder="ej: % conformidad MRSL"
              value={formData.kpi}
              onChange={(e) => setFormData({ ...formData, kpi: e.target.value })}
            />
            <Form.Input
              label="Unidad"
              placeholder="ej: %"
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            />
            <Form.Input
              label="Línea base"
              placeholder="ej: 8.5 (2024)"
              value={formData.baseline}
              onChange={(e) => setFormData({ ...formData, baseline: e.target.value })}
            />
            <Form.Input
              label="Meta (valor y fecha)"
              placeholder="ej: 7.2 (dic-2026)"
              value={formData.target}
              onChange={(e) => setFormData({ ...formData, target: e.target.value })}
            />
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Input
              label="Plazo"
              type="date"
              value={formData.deadline ? formData.deadline.split("T")[0] : ""}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
            />
            <Form.Field>
              <label>Relevancia (1-5)</label>
              <Input
                type="number"
                min="1"
                max="5"
                value={formData.relevance}
                onChange={(e) =>
                  setFormData({ ...formData, relevance: Number(e.target.value) })
                }
              />
            </Form.Field>
            <Form.Field>
              <label>Factibilidad (1-5)</label>
              <Input
                type="number"
                min="1"
                max="5"
                value={formData.feasibility}
                onChange={(e) =>
                  setFormData({ ...formData, feasibility: Number(e.target.value) })
                }
              />
            </Form.Field>
          </Form.Group>

          <Form.TextArea
            label="Plan de acción (alcanzable)"
            placeholder="Describa las acciones concretas..."
            value={formData.actionPlan}
            onChange={(e) => setFormData({ ...formData, actionPlan: e.target.value })}
          />

          <Form.Group widths="equal">
            <Form.Input
              label="Presupuesto (viabilidad)"
              placeholder="ej: 0"
              value={formData.budget}
              onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
            />
            <Form.Field>
              <label>Avance %</label>
              <Input
                type="number"
                min="0"
                max="100"
                value={formData.progress}
                onChange={(e) => setFormData({ ...formData, progress: Number(e.target.value) })}
              />
            </Form.Field>
            <Form.Field>
              <label>Estado</label>
              <Dropdown
                selection
                options={estadoOptions}
                value={formData.status}
                onChange={(e, { value }) => setFormData({ ...formData, status: value })}
              />
            </Form.Field>
            <Form.Field>
              <label>Etapa PDCA</label>
              <Dropdown
                selection
                options={etapaOptions}
                value={formData.stage}
                onChange={(e, { value }) => setFormData({ ...formData, stage: value })}
              />
            </Form.Field>
          </Form.Group>

          <Form.TextArea
            label="Evidencias/Registros"
            placeholder="Documentos, fotos, informes..."
            value={formData.evidence}
            onChange={(e) => setFormData({ ...formData, evidence: e.target.value })}
          />

          <Message info>
            <Message.Header>SMART:</Message.Header>
            <p>
              <strong>S</strong>: Específico (objetivo claro) |{" "}
              <strong>M</strong>: Medible (KPI + unidad + meta) |{" "}
              <strong>A</strong>: Alcanzable (factibilidad ≥ 3) |{" "}
              <strong>R</strong>: Relevante (relevancia ≥ 3) |{" "}
              <strong>T</strong>: Tiempo (plazo definido)
            </p>
          </Message>

          <Button primary onClick={handleSaveObjective} icon labelPosition="left">
            <Icon name="plus" />
            Añadir Objetivo
          </Button>
        </Form>
      </Segment>

      {/* Tabla de objetivos */}
      <Segment style={{ marginTop: "20px" }}>

        {/* Tabla de objetivos con campos editables */}
        <div style={{ overflowX: "auto" }}>
          <Table celled compact size="small" style={{ minWidth: "1800px" }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Eje</Table.HeaderCell>
                <Table.HeaderCell>Objetivo SMART</Table.HeaderCell>
                <Table.HeaderCell>KPI</Table.HeaderCell>
                <Table.HeaderCell>Unidad</Table.HeaderCell>
                <Table.HeaderCell>Línea base</Table.HeaderCell>
                <Table.HeaderCell>Meta</Table.HeaderCell>
                <Table.HeaderCell>Plazo</Table.HeaderCell>
                <Table.HeaderCell>Responsable</Table.HeaderCell>
                <Table.HeaderCell>Rel.</Table.HeaderCell>
                <Table.HeaderCell>Fact.</Table.HeaderCell>
                <Table.HeaderCell>Prioridad</Table.HeaderCell>
                <Table.HeaderCell>Plan de acción</Table.HeaderCell>
                <Table.HeaderCell>Presupuesto</Table.HeaderCell>
                <Table.HeaderCell>Avance %</Table.HeaderCell>
                <Table.HeaderCell>Estado</Table.HeaderCell>
                <Table.HeaderCell>SMART</Table.HeaderCell>
                <Table.HeaderCell>PDCA</Table.HeaderCell>
                <Table.HeaderCell>Evidencias</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Acciones</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredObjectives.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan="19" textAlign="center">
                    <Message info>
                      <Message.Header>No hay objetivos</Message.Header>
                      <p>Agregue objetivos usando el formulario de arriba</p>
                    </Message>
                  </Table.Cell>
                </Table.Row>
              ) : (
                filteredObjectives.map((obj) => {
                  const smart = calculateSMART(obj);
                  const priority = (obj.relevance || 3) * (obj.feasibility || 3);
                  const getPriorityColor = (p) => {
                    if (p >= 20) return "red";
                    if (p >= 12) return "orange";
                    if (p >= 6) return "yellow";
                    return "grey";
                  };
                  return (
                    <Table.Row key={obj._id}>
                      {/* Eje */}
                      <Table.Cell>
                        <Label color="blue" size="tiny">
                          {obj.axis || "—"}
                        </Label>
                      </Table.Cell>
                      {/* Objetivo SMART */}
                      <Table.Cell style={{ minWidth: "200px" }}>
                        <strong>{obj.objective || "—"}</strong>
                      </Table.Cell>
                      {/* KPI */}
                      <Table.Cell>{obj.kpi || "—"}</Table.Cell>
                      {/* Unidad */}
                      <Table.Cell>{obj.unit || "—"}</Table.Cell>
                      {/* Línea base */}
                      <Table.Cell>{obj.baseline || "—"}</Table.Cell>
                      {/* Meta */}
                      <Table.Cell>{obj.target || "—"}</Table.Cell>
                      {/* Plazo - Editable */}
                      <Table.Cell>
                        <Input
                          type="date"
                          size="mini"
                          value={obj.deadline ? obj.deadline.split("T")[0] : ""}
                          onChange={(e) =>
                            handleInlineUpdate(obj._id, "deadline", e.target.value)
                          }
                          style={{ width: "130px" }}
                        />
                      </Table.Cell>
                      {/* Responsable */}
                      <Table.Cell>{obj.responsible || "—"}</Table.Cell>
                      {/* Relevancia - Editable */}
                      <Table.Cell textAlign="center">
                        <Input
                          type="number"
                          min="1"
                          max="5"
                          size="mini"
                          value={obj.relevance || 3}
                          onChange={(e) =>
                            handleInlineUpdate(obj._id, "relevance", Number(e.target.value))
                          }
                          style={{ width: "55px" }}
                        />
                      </Table.Cell>
                      {/* Factibilidad - Editable */}
                      <Table.Cell textAlign="center">
                        <Input
                          type="number"
                          min="1"
                          max="5"
                          size="mini"
                          value={obj.feasibility || 3}
                          onChange={(e) =>
                            handleInlineUpdate(obj._id, "feasibility", Number(e.target.value))
                          }
                          style={{ width: "55px" }}
                        />
                      </Table.Cell>
                      {/* Prioridad (calculada: Rel × Fact) */}
                      <Table.Cell textAlign="center">
                        <Label color={getPriorityColor(priority)} size="small">
                          {priority}
                        </Label>
                      </Table.Cell>
                      {/* Plan de acción */}
                      <Table.Cell style={{ maxWidth: "150px" }}>
                        <div style={{ fontSize: "0.85em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={obj.actionPlan}>
                          {obj.actionPlan || "—"}
                        </div>
                      </Table.Cell>
                      {/* Presupuesto */}
                      <Table.Cell>{obj.budget || "—"}</Table.Cell>
                      {/* Avance % - Editable */}
                      <Table.Cell>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          size="mini"
                          value={obj.progress || 0}
                          onChange={(e) =>
                            handleInlineUpdate(obj._id, "progress", Number(e.target.value))
                          }
                          style={{ width: "70px" }}
                          label={{ basic: true, content: "%" }}
                          labelPosition="right"
                        />
                      </Table.Cell>
                      {/* Estado */}
                      <Table.Cell>
                        <Label color={getEstadoColor(obj.status)} size="tiny">
                          {obj.status || "Planned"}
                        </Label>
                      </Table.Cell>
                      {/* SMART Score */}
                      <Table.Cell textAlign="center">
                        <Label color={getSMARTColor(smart.score)} circular size="small">
                          {smart.score}/5
                        </Label>
                      </Table.Cell>
                      {/* PDCA - Editable */}
                      <Table.Cell>
                        <Dropdown
                          selection
                          compact
                          options={etapaOptions}
                          value={obj.stage || "Plan"}
                          onChange={(_, { value }) =>
                            handleInlineUpdate(obj._id, "stage", value)
                          }
                          style={{ minWidth: "80px" }}
                        />
                      </Table.Cell>
                      {/* Evidencias */}
                      <Table.Cell style={{ maxWidth: "120px" }}>
                        <div style={{ fontSize: "0.85em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={obj.evidence}>
                          {obj.evidence || "—"}
                        </div>
                      </Table.Cell>
                      {/* Acciones: Copiar y Eliminar */}
                      <Table.Cell textAlign="center">
                        <Button.Group size="mini">
                          <Button
                            icon="copy"
                            title="Copiar objetivo"
                            onClick={() => handleCopyObjective(obj)}
                          />
                          <Button
                            icon="trash"
                            color="red"
                            title="Eliminar objetivo"
                            onClick={() => handleDeleteObjective(obj._id)}
                          />
                        </Button.Group>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table>
        </div>
      </Segment>
    </div>
  );
}
